import React, { useState } from "react";

import { IDog, IDogRun } from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants";

import Button from "../../../components/Button/Button";
import DatePick from "../../../components/DatePick/DatePick";
import Dropdown from "../../../components/Dropdown/Dropdown";
import Input from "../../../components/Input/Input";
import TextArea from "../../../components/TextArea/TextArea";

import { formatRunDate, parseRunDate, runOption, runOptions } from "./runFormFields";

import "./ObedienceRunForm.scss"; // Reuse the same styling

interface IProps {
  dog: IDog;
  onSubmit: (data: any) => void;
  onCancel: () => void;
  isSubmitting: boolean;
  editRun?: IDogRun;
}

const BARN_HUNT_LEVELS = [
  { value: Constants.dog_class_element_level.barn_hunt_instinct, label: 'Instinct' },
  { value: Constants.dog_class_element_level.barn_hunt_novice, label: 'Novice' },
  { value: Constants.dog_class_element_level.barn_hunt_open, label: 'Open' },
  { value: Constants.dog_class_element_level.barn_hunt_senior, label: 'Senior' },
  { value: Constants.dog_class_element_level.barn_hunt_master, label: 'Master' },
  { value: Constants.dog_class_element_level.barn_hunt_crazy_8s, label: 'Crazy 8s' },
  { value: Constants.dog_class_element_level.barn_hunt_line_drive, label: 'Line Drive' }
];

const BARN_HUNT_SECTIONS = [
  { value: 'A', label: 'A' },
  { value: 'B', label: 'B' }
];

const RESULT_STATUSES = [
  { value: Constants.score_result_type.qualified, label: 'Q (Qualified)' },
  { value: Constants.score_result_type.not_qualified, label: 'NQ (Not Qualified)' },
  { value: Constants.score_result_type.absent, label: 'ABS (Absent)' }
];

const BarnHuntRunForm: React.FC<IProps> = ({ dog, onSubmit, onCancel, isSubmitting, editRun }) => {
  // Form fields
  const [trialDate, setTrialDate] = useState<string>(
    editRun?.trial_date ? new Date(editRun.trial_date).toISOString().split('T')[0] : ''
  );
  const [level, setLevel] = useState<string>(editRun?.level || '');
  const [section, setSection] = useState<string>(editRun?.section || 'A');
  const [resultStatus, setResultStatus] = useState<string>(editRun?.result_status || '');
  const [time, setTime] = useState<string>(editRun?.time || ''); // MM:SS or M:SS
  const [judgeNumber, setJudgeNumber] = useState<string>(editRun?.judge_number || '');
  const [judgeName, setJudgeName] = useState<string>(editRun?.judge_name || '');
  const [eventName, setEventName] = useState<string>(editRun?.event_name || '');
  const [city, setCity] = useState<string>(editRun?.location?.city || '');
  const [state, setState] = useState<string>(editRun?.location?.state || '');
  const [notes, setNotes] = useState<string>(editRun?.notes || '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Parse the date and set it to noon local time to avoid timezone issues
    const [year, month, day] = trialDate.split('-').map(Number);
    const localDate = new Date(year, month - 1, day, 12, 0, 0);
    
    const data: any = {
      dogId: dog._id,
      level,
      section,
      resultStatus,
      time: time || undefined,
      trialDate: localDate,
      judgeNumber: judgeNumber || undefined,
      judgeName: judgeName || undefined,
      eventName: eventName || undefined,
      location: (city || state) ? { city, state } : undefined,
      notes: notes || undefined
    };

    if (editRun) {
      data.run_id = editRun._id;
    }

    onSubmit(data);
  };

  const levelOptions = runOptions(BARN_HUNT_LEVELS, "Select Level");
  const sectionOptions = runOptions(BARN_HUNT_SECTIONS);
  const resultOptions = runOptions(RESULT_STATUSES, "Select Result");
  const isValid = trialDate && level && section && resultStatus;

  return (
    <form onSubmit={handleSubmit} className="obedienceRunForm">
      <div className="formGrid">
        <div className="fieldGroup">
          <DatePick
            label="Trial Date *"
            placeholder="Select date"
            value={parseRunDate(trialDate)}
            onChange={(date: Date | null) => setTrialDate(formatRunDate(date))}
          />
        </div>

        <div className="fieldGroup">
          <Dropdown
            label="Level *"
            placeholder="Select Level"
            options={levelOptions}
            value={runOption(levelOptions, level)}
            onChange={(_e, option) => setLevel(option.id)}
          />
        </div>

        <div className="fieldGroup">
          <Dropdown
            label="Section *"
            placeholder="Select Section"
            options={sectionOptions}
            value={runOption(sectionOptions, section)}
            onChange={(_e, option) => setSection(option.id)}
          />
          <span className="fieldHelp">
            A = First attempt at level, B = Repeat runs for X-level titles
          </span>
        </div>

        <div className="fieldGroup">
          <Dropdown
            label="Result *"
            placeholder="Select Result"
            options={resultOptions}
            value={runOption(resultOptions, resultStatus)}
            onChange={(_e, option) => setResultStatus(option.id)}
          />
        </div>

        <div className="fieldGroup">
          <Input
            label={level === "barn_hunt_crazy_8s" ? "Time *" : "Time"}
            id="time"
            type="text"
            value={time}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTime(e.target.value)}
            placeholder="MM:SS or M:SS (e.g., 2:30)"
            pattern="^\d{1,2}:\d{2}(\.\d{1,2})?$"
            required={level === "barn_hunt_crazy_8s"}
          />
          <span className="fieldHelp">
            Format: MM:SS or M:SS (e.g., 2:30 or 02:30.50)
          </span>
        </div>

        <div className="fieldGroup">
          <Input
            label="Judge Number"
            id="judgeNumber"
            type="text"
            value={judgeNumber}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setJudgeNumber(e.target.value)}
            placeholder="Optional"
          />
        </div>

        <div className="fieldGroup">
          <Input
            label="Judge Name"
            id="judgeName"
            type="text"
            value={judgeName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setJudgeName(e.target.value)}
            placeholder="Optional"
          />
        </div>

        <div className="fieldGroup">
          <Input
            label="Event Name"
            id="eventName"
            type="text"
            value={eventName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEventName(e.target.value)}
            placeholder="e.g., Summer Barn Hunt Trial"
          />
        </div>

        <div className="fieldGroup">
          <Input
            label="City"
            id="city"
            type="text"
            value={city}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCity(e.target.value)}
            placeholder="e.g., San Francisco"
          />
        </div>

        <div className="fieldGroup">
          <Input
            label="State"
            id="state"
            type="text"
            value={state}
            maxLength={2}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setState(e.target.value.toUpperCase())}
            placeholder="e.g., CA"
          />
        </div>

        <div className="fieldGroup fullWidth">
          <TextArea
            className="runNotes"
            label="Notes"
            placeholder="Optional notes about the run..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>
      </div>

      {/* Form Actions */}
      <div className="formActions">
        <Button 
          onClick={onCancel}
          variant="secondary"
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button 
          type="submit"
          disabled={!isValid || isSubmitting}
        >
          {isSubmitting ? 'Saving...' : editRun ? 'Update Run' : 'Add Run'}
        </Button>
      </div>
    </form>
  );
};

export default BarnHuntRunForm;

